import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import PromoBanner from "@/components/PromoBanner";
import KeyboardSoundToggle from "@/components/KeyboardSoundToggle";
import DiffView from "@/components/DiffView";
import { keyClickHandler } from "@/lib/keyboardSound";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { AlertCircle, CheckCircle2, ChevronRight, Undo2, Zap, Target } from "lucide-react";

function ModuleTag({ module }) {
  const colors = {
    dictation: "bg-brand-blue/10 border-brand-blue/40 text-brand-blue",
    legal:     "bg-brand-gold/10 border-brand-gold/40 text-brand-gold",
    pitman:    "bg-brand-purple/10 border-brand-purple/40 text-brand-purple",
    typing:    "bg-brand-emerald/10 border-brand-emerald/40 text-brand-emerald",
  };
  return (
    <span className={`inline-block px-2 py-0.5 text-[10px] uppercase tracking-widest font-bold border ${colors[module] || "border-border"}`}>
      {module}
    </span>
  );
}

function DrillCard({ item, onDone }) {
  const [typed, setTyped] = useState("");
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const onKey = useMemo(() => keyClickHandler(), []);
  const taRef = useRef(null);

  useEffect(() => { setTimeout(() => taRef.current?.focus(), 40); }, [item.id]);

  const submit = async () => {
    if (!typed.trim() || submitting) return;
    setSubmitting(true);
    try {
      const { data } = await api.post(`/mistake-practice/${item.id}/attempt`, { typed_text: typed });
      setResult(data);
      if (data.mastered) {
        toast.success(`Mastered! ${data.accuracy}% accuracy — moving on`, { duration: 3500 });
      } else {
        toast.info(`${data.accuracy}% accuracy — try again to master this sentence`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Submit failed");
    } finally {
      setSubmitting(false);
    }
  };

  const next = () => onDone?.(result?.mastered);
  const skip = async () => {
    if (!window.confirm("Mark this as mastered without drilling? It won't come back.")) return;
    try {
      await api.post(`/mistake-practice/${item.id}/master`);
      toast.success("Marked as mastered");
      onDone?.(true);
    } catch (e) {
      toast.error("Skip failed");
    }
  };

  return (
    <div className="border-2 border-border p-6 sm:p-8 bg-card" data-testid={`mistake-drill-${item.id}`}>
      <div className="flex items-center gap-3 mb-3 text-xs">
        <ModuleTag module={item.module} />
        <span className="font-mono text-muted-foreground">
          Missed {item.times_wrong}× · {item.word_count} words
          {item.last_accuracy != null && ` · last ${item.last_accuracy}%`}
        </span>
      </div>

      <div className="border-l-4 border-brand-crimson/40 bg-brand-crimson/10 p-5 mb-5">
        <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-brand-crimson mb-2">Read carefully</div>
        <p className="text-lg leading-relaxed font-medium" data-testid="mistake-sentence">
          {item.sentence}
        </p>
      </div>

      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-muted-foreground">Type it below</div>
        <KeyboardSoundToggle />
      </div>
      <Textarea
        ref={taRef}
        value={typed}
        onChange={(e) => setTyped(e.target.value)}
        onKeyDown={onKey}
        placeholder="Type the sentence exactly…"
        rows={4}
        disabled={submitting || !!result?.mastered}
        className="font-mono text-base leading-relaxed border-border focus-visible:border-brand-gold rounded-lg"
        data-testid="mistake-typing-area"
      />

      <div className="mt-5 flex flex-wrap items-center gap-3">
        {!result && (
          <>
            <Button
              onClick={submit}
              disabled={submitting || !typed.trim()}
              className="rounded-lg h-11 bg-brand-gold hover:bg-brand-gold/90 text-white text-xs uppercase tracking-[0.2em] font-bold"
              data-testid="mistake-submit-btn"
            >
              {submitting ? "Checking…" : "Check my typing"}
            </Button>
            <button onClick={skip} className="h-11 px-4 border border-border text-xs uppercase tracking-widest font-bold hover:bg-secondary/60" data-testid="mistake-skip-btn">
              I know this — dismiss
            </button>
          </>
        )}
        {result && !result.mastered && (
          <>
            <Button
              onClick={() => { setResult(null); setTyped(""); setTimeout(() => taRef.current?.focus(), 40); }}
              className="rounded-lg h-11 bg-brand-gold hover:bg-brand-gold/90 text-white text-xs uppercase tracking-[0.2em] font-bold"
              data-testid="mistake-retry-btn"
            >
              <Undo2 className="w-4 h-4 mr-2" /> Try again
            </Button>
            <button onClick={next} className="h-11 px-4 border border-border text-xs uppercase tracking-widest font-bold hover:bg-secondary/60" data-testid="mistake-next-btn">
              Skip to next
            </button>
          </>
        )}
        {result?.mastered && (
          <Button onClick={next} className="rounded-lg h-11 bg-[#059669] hover:bg-[#047857] text-white text-xs uppercase tracking-[0.2em] font-bold" data-testid="mistake-continue-btn">
            <ChevronRight className="w-4 h-4 mr-2" /> Next sentence
          </Button>
        )}
      </div>

      {result?.diff && (
        <div className="mt-8">
          <DiffView result={result.diff} />
        </div>
      )}
    </div>
  );
}

export default function MistakePractice() {
  const [queue, setQueue] = useState(null);
  const [idx, setIdx] = useState(0);
  const [stats, setStats] = useState(null);

  const load = async () => {
    try {
      const [q, s] = await Promise.all([
        api.get("/mistake-practice/queue", { params: { limit: 30 } }),
        api.get("/mistake-practice/stats"),
      ]);
      setQueue(q.data.items);
      setStats(s.data);
      setIdx(0);
    } catch { /* ignore */ }
  };

  useEffect(() => { load(); }, []);

  const current = queue?.[idx];
  const remaining = queue ? Math.max(queue.length - idx - 1, 0) : 0;

  const handleDone = (mastered) => {
    // Move to next in the local queue. Refresh from server every 3 items to
    // pick up any new mistakes added by other tabs / recent attempts.
    if ((idx + 1) % 3 === 0) {
      load();
    } else {
      setIdx((i) => i + 1);
    }
    setStats((s) => s ? { ...s, pending: Math.max(0, s.pending - (mastered ? 1 : 0)), mastered: (s.mastered || 0) + (mastered ? 1 : 0) } : s);
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <PromoBanner />
      <Navbar />
      <main className="max-w-4xl mx-auto px-6 py-12">
        <div className="mb-10">
          <div className="text-xs uppercase tracking-[0.3em] font-bold text-brand-crimson mb-3">Mistake practice</div>
          <h1 className="text-4xl lg:text-5xl font-black tracking-tight" data-testid="mistake-practice-title">
            Fix what you missed.
          </h1>
          <p className="mt-3 font-mono text-muted-foreground max-w-2xl">
            Every sentence you typed wrong in the last few days lives here. Drill it once at 95%+ accuracy and it's marked mastered — off the queue forever.
          </p>
        </div>

        {/* Stats strip */}
        {stats && (
          <div className="grid grid-cols-2 gap-3 mb-10">
            <div className="border-2 border-brand-crimson/40 bg-brand-crimson/10 p-5" data-testid="mistake-stats-pending">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] font-bold text-brand-crimson mb-2">
                <AlertCircle className="w-3.5 h-3.5" /> Pending
              </div>
              <div className="text-4xl font-black tracking-tight">{stats.pending}</div>
              <div className="text-[11px] font-mono text-muted-foreground mt-1">sentences to drill</div>
            </div>
            <div className="border-2 border-brand-emerald/40 bg-brand-emerald/10 p-5" data-testid="mistake-stats-mastered">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] font-bold text-brand-emerald mb-2">
                <CheckCircle2 className="w-3.5 h-3.5" /> Mastered
              </div>
              <div className="text-4xl font-black tracking-tight">{stats.mastered}</div>
              <div className="text-[11px] font-mono text-muted-foreground mt-1">lifetime</div>
            </div>
          </div>
        )}

        {queue == null && (
          <div className="text-sm font-mono text-muted-foreground" data-testid="mistake-loading">Loading your queue…</div>
        )}

        {queue && queue.length === 0 && (
          <div className="border-2 border-border p-10 text-center" data-testid="mistake-empty">
            <div className="w-14 h-14 border-2 border-brand-emerald/40 bg-brand-emerald/10 flex items-center justify-center mx-auto mb-4">
              <Target className="w-6 h-6 text-brand-emerald" />
            </div>
            <div className="text-2xl font-black tracking-tight mb-2">Nothing to fix — nice work.</div>
            <p className="text-sm font-mono text-muted-foreground max-w-md mx-auto mb-6">
              Any sentence you type at &lt;90% accuracy in future practice will show up here. Keep practising.
            </p>
            <Link to="/dashboard">
              <Button className="rounded-lg h-11 bg-brand-gold hover:bg-brand-gold/90 text-white text-xs uppercase tracking-[0.2em] font-bold px-6">
                Back to dashboard
              </Button>
            </Link>
          </div>
        )}

        {current && (
          <>
            <div className="mb-4 flex items-center justify-between">
              <div className="text-xs font-mono text-muted-foreground">
                Sentence <span className="text-black font-bold">{idx + 1}</span> / {queue.length}
              </div>
              <div className="text-xs font-mono text-muted-foreground flex items-center gap-2">
                <Zap className="w-3.5 h-3.5 text-brand-crimson" /> {remaining} more after this
              </div>
            </div>
            <DrillCard item={current} onDone={handleDone} />
          </>
        )}

        {queue && queue.length > 0 && !current && (
          <div className="border-2 border-brand-emerald/40 bg-brand-emerald/10 p-10 text-center" data-testid="mistake-session-done">
            <div className="w-14 h-14 border-2 border-brand-emerald/40 bg-card flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-6 h-6 text-brand-emerald" />
            </div>
            <div className="text-2xl font-black tracking-tight mb-2">Session complete!</div>
            <p className="text-sm font-mono text-muted-foreground max-w-md mx-auto mb-6">
              You've drilled every sentence in this batch. Come back after more practice for a fresh queue.
            </p>
            <button
              onClick={load}
              className="h-11 px-6 border-2 border-brand-gold bg-white text-foreground hover:bg-[#0A2540] hover:text-white text-xs uppercase tracking-[0.2em] font-bold transition-colors"
              data-testid="mistake-refresh-btn"
            >
              Reload queue
            </button>
          </div>
        )}
      </main>
    </div>
  );
}
